import {
    forwardRef,
    memo
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type ICalendarCellProps from "./type";
import stylesheet, {
    getCalendarCellTitleVariant,
    getCalendarCellIsFilled,
    getCalendarCellColors,
    getCalendarCellLabel,
    getCalendarCellTitle,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    useReducedMotion,
    usePressScale
} from "../../helpers/motion";
import type {
    RefForwardingComponent
} from "../../types";
import StateLayer from "../stateLayer";
import Text from "../text";

const CalendarCell: RefForwardingComponent<View, ICalendarCellProps> = ({
    spreadBehaviour = "baseline",
    "aria-hidden": ariaHidden,
    "aria-label": ariaLabel,
    calendarType = "day",
    accessibilityLabel,
    isDisabled = false,
    isSelected = false,
    isOutside = false,
    isToday = false,
    role = "button",
    customLocalize,
    rangePosition,
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    date,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        durations,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled: isDisabled || !onPress,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isReduceMotionEnabled = useReducedMotion();

    const isInteractive = !!onPress;

    const pressScaleStyle = usePressScale({
        isDisabled: isDisabled || !isInteractive,
        customTheme,
        isPressed
    });

    const isFilled = getCalendarCellIsFilled({
        rangePosition,
        isSelected
    });

    const isInSelection = isFilled || !!rangePosition;

    const currentColors = getCalendarCellColors({
        rangePosition,
        isDisabled,
        isOutside,
        isFilled,
        isToday,
        colors
    });

    const currentTitleVariant = getCalendarCellTitleVariant({
        isFilled,
        isToday
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        title: titleDynamicStyle
    } = useStyles({
        isFocusVisible: isInteractive && isFocusVisible,
        isReduceMotionEnabled,
        spreadBehaviour,
        controlHeights,
        currentColors,
        rangePosition,
        shapeConfigs,
        shadowAlpha,
        elevations,
        isDisabled,
        durations,
        isFilled,
        radiuses,
        borders,
        colors
    });

    const renderStateLayer = () => {
        if(!isInteractive) {
            return null;
        }

        return <StateLayer
            borderRadius={radiuses.actions}
            customTheme={customTheme}
            overlayColor="default"
            isHovered={isHovered}
            isPressed={isPressed}
            isSquircle={true}
        />;
    };

    const renderSurface = () => {
        return <Animated.View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle,
                pressScaleStyle
            ]}
        >
            {renderStateLayer()}
            <Text
                customColor={currentColors.titleColor}
                variant={currentTitleVariant}
                customTheme={customTheme}
                style={[
                    stylesheet.title,
                    titleDynamicStyle
                ]}
                numberOfLines={1}
            >
                {title ?? getCalendarCellTitle({
                    activeLocale,
                    calendarType,
                    date
                })}
            </Text>
        </Animated.View>;
    };

    if(!isInteractive) {
        return <View
            {...props}
            aria-hidden={ariaHidden ?? true}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            ref={ref}
        >
            {renderSurface()}
        </View>;
    }

    const selectionProps = Platform.OS === "web" ? {
        "aria-pressed": isInSelection ? true : undefined,
        "aria-current": isToday ? "date" : undefined
    } : {
        "aria-selected": isInSelection ? true : undefined
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...selectionProps}
        aria-label={ariaLabel ?? accessibilityLabel ?? getCalendarCellLabel({
            rangePosition,
            activeLocale,
            calendarType,
            isDisabled,
            localize,
            isToday,
            date
        })}
        aria-disabled={isDisabled}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-hidden={ariaHidden}
        disabled={isDisabled}
        onPress={onPress}
        role={role}
        ref={ref}
    >
        {renderSurface()}
    </Pressable>;
};

const isCalendarCellPropsEqual = (previousProps: ICalendarCellProps, nextProps: ICalendarCellProps) => {
    const previousKeys = Object.keys(previousProps) as Array<keyof ICalendarCellProps>;

    if(previousKeys.length !== Object.keys(nextProps).length) {
        return false;
    }

    return previousKeys.every((key) => {
        const previousValue = previousProps[key];

        const nextValue = nextProps[key];

        if(previousValue instanceof Date && nextValue instanceof Date) {
            return previousValue.getTime() === nextValue.getTime();
        }

        return Object.is(previousValue, nextValue);
    });
};
export default memo(forwardRef(CalendarCell), isCalendarCellPropsEqual);
